import React from 'react';
import {MultiValue, SingleValue} from 'react-select';
import ImagesManualSelectionFilter from './ImagesManualSelectionFilter';
import ImagesAutomaticSelectionFilter from './ImagesAutomaticSelectionFilter';
import SpecialToolFilter from './SpecialToolFilter';
import {FetchOptionType, IOptionType} from '../../types/common';
import {IImageManualModel, IManualFinderFilterModel, IManualFinderModel, IManualModel} from "../../types/manualFinder";
import {ExtractedItem} from '../../utils/manualFinderUtils';
import {mapManualItemToManual} from "../../utils/modelBuilder";
import {refineImagesAutomaticSelection} from "../../services/manualFinder/refineImagesAutomaticSelection";
import {refineSpecialTools} from "../../services/manualFinder/refineSpecialTools";
import {useLoadOptions} from "../../hooks/useLoadOptions";

interface IManualFinderFilterProps {
    selectedExtractedType: IOptionType;
    manualFinderFilterProps: IManualFinderFilterModel;
    handleValueChange: (value: number | SingleValue<IOptionType> | MultiValue<IOptionType> | boolean[], key: keyof IManualFinderFilterModel) => void;
    isLoading: boolean;
    t: (key: string) => string;
    specialTools: string[];
    setManualFinderProps: (manualFinderProps: IManualFinderModel) => void;
    manualFinderProps: IManualFinderModel;
    setIsLoading: (isLoading: boolean) => void
}

export const ManualFinderFilter: React.FC<IManualFinderFilterProps> = ({
    selectedExtractedType,
    manualFinderFilterProps,
    handleValueChange,
    isLoading,
    t,
    specialTools,
    manualFinderProps,
    setManualFinderProps,
    setIsLoading
}) => {
    const {manufacturerOptions} = useLoadOptions([FetchOptionType.Manufacturer]);
    const onRefineImagesAutomatic = async () => {
        const imageNames = manualFinderProps.extractedImages
            .map((obj: any) => Object.keys(obj))
            .flat();

        const filter = manualFinderFilterProps.selectedManufacturers.length > 0
            ? {
                "$or": manualFinderFilterProps.selectedManufacturers.map(manufacturer => ({
                    "Manufacturer": manufacturer.value
                }))
            }
            : {};

        const { manualsBasedOnImages, newExtractedImages } =
            await refineImagesAutomaticSelection(imageNames, manualFinderFilterProps, filter);

        const matchingManuals = manualsBasedOnImages
            .filter((item: IImageManualModel) => item !== null);

        setManualFinderProps({
            ...manualFinderProps,
            manualsBasedOnImages: matchingManuals,
            extractedImages: newExtractedImages
        });
    };

    const onFilterMatches = async (filteredTools: string[], topK: number) => {
        const formatedTools = JSON.stringify(filteredTools);
        const manualResponse = await refineSpecialTools(formatedTools, topK);

        if (manualResponse.length > 0) {
            const matchingManuals: IManualModel[] = manualResponse
                .filter((item: any) => item !== null)
                .map(mapManualItemToManual);
            setManualFinderProps({
                ...manualFinderProps,
                manuals: matchingManuals
            });
        }
    };

    const handleApplyFilter = async (event: any) => {
        event.preventDefault();
        setIsLoading(true);
        try {
            if (selectedExtractedType.value === ExtractedItem.SpecialTools) {
                const selectedTools = specialTools.filter((_, index) => manualFinderFilterProps.checkedTools[index]);
                await onFilterMatches(selectedTools, manualFinderFilterProps.topKSpecialTools);
            } else {
                await onRefineImagesAutomatic();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    switch (selectedExtractedType.value) {
        case ExtractedItem.ImagesManual:
            return (
                <ImagesManualSelectionFilter
                    manufacturerOptions={manufacturerOptions}
                    selectedManufacturers={manualFinderFilterProps.selectedManufacturers}
                    onChangeManufacturers={selectedItems =>
                        handleValueChange(selectedItems, 'selectedManufacturers')
                    }
                    handleApplyFilter={handleApplyFilter}
                    isLoading={isLoading}
                    t={t}
                />
            );
        case ExtractedItem.ImagesAutomatic:
            return (
                <ImagesAutomaticSelectionFilter
                    manufacturerOptions={manufacturerOptions}
                    threshold={manualFinderFilterProps.threshold}
                    maxNumberOfManuals={manualFinderFilterProps.maxNumberOfManuals}
                    selectedManufacturers={manualFinderFilterProps.selectedManufacturers}
                    onThresholdChange={(e) => handleValueChange(parseFloat(e.target.value), 'threshold')}
                    onNumberChange={(e) => handleValueChange(parseInt(e.target.value, 10), 'maxNumberOfManuals')}
                    onChangeManufacturers={(items) => handleValueChange(items, 'selectedManufacturers')}
                    handleApplyFilter={handleApplyFilter}
                    isLoading={isLoading}
                    t={t}
                />
            );
        case ExtractedItem.SpecialTools:
            return (
                <SpecialToolFilter
                    specialTools={specialTools}
                    checkedTools={manualFinderFilterProps.checkedTools}
                    onCheckboxChange={(index) => {
                        const updatedCheckedTools = [...manualFinderFilterProps.checkedTools];
                        updatedCheckedTools[index] = !updatedCheckedTools[index];
                        handleValueChange(updatedCheckedTools, 'checkedTools')
                    }}
                    topK={manualFinderFilterProps.topKSpecialTools}
                    onNumberChange={(e) => handleValueChange(parseInt(e.target.value, 10), 'topKSpecialTools')}
                    handleApplyFilter={handleApplyFilter}
                    isLoading={isLoading}
                    t={t}
                />
            );
        default:
            return <></>;
    }
};
